export interface LaunchpadLink {
  name: string;
  url: string;
  sortIndex: number;
}

export interface LaunchpadAttributes {
  name: string;
  description: string;
  logoURL?: string | null;
  campusID?: string | null;
  categoryID?: string | null;
  links: LaunchpadLink[];
}

export interface LaunchpadMetadata {
  churchID: string;
  launchpadID: string;
  attributes: LaunchpadAttributes;
}

export interface Launchpad {
  churchID: string;
  launchpadID: string;
  // The short, globally unique handle it can be reached by, or null when the
  // church hasn't claimed one. Undefined means not known yet: a Launchpad built
  // from list metadata, before its own fetch has come back.
  tag?: string | null;
  metadata: LaunchpadMetadata;
}

// Where Launchpad-Web is served, on its short domain since these links get
// shared. Set REACT_APP_LAUNCHPAD_URL to point somewhere else; otherwise it
// follows whichever Sanctuary this build talks to.
export const LAUNCHPAD_WEB_URL = process.env.REACT_APP_LAUNCHPAD_URL ||
  (process.env.REACT_APP_SANCTUARY_API_URL?.includes('staging') ?
    'https://staging.launchpad.church' :
    'https://launchpad.church');

// Sanctuary's tag rules (LaunchpadTag.normalized): 3 to 32 lower case
// letters, numbers and hyphens, not starting or ending with a hyphen. Checked
// here so a bad tag is caught while typing rather than on save.
export const LAUNCHPAD_TAG_LENGTH = { min: 3, max: 32 };

export function normalizedLaunchpadTag(tag: string) {
  return tag.trim().toLowerCase();
}

// Why `tag` can't be used, or null when it can. Expects a normalized tag.
export function launchpadTagProblem(tag: string): string | null {
  if (tag.length < LAUNCHPAD_TAG_LENGTH.min || tag.length > LAUNCHPAD_TAG_LENGTH.max) {
    return `A tag must be between ${LAUNCHPAD_TAG_LENGTH.min} and ${LAUNCHPAD_TAG_LENGTH.max} characters.`;
  }

  if (!/^[a-z0-9-]+$/.test(tag) || tag.startsWith('-') || tag.endsWith('-')) {
    return "A tag may only contain letters, numbers and hyphens, and can't start or end with a hyphen.";
  }

  return null;
}

// A playlist's page on Launchpad-Web, where anyone can open it. Mirrors
// playlistDetailPath in Launchpad-Web.
export function playlistLaunchpadURL(playlist: { churchID: string; id: string }) {
  return `${LAUNCHPAD_WEB_URL}/church/${encodeURIComponent(playlist.churchID)}/playlist/${encodeURIComponent(playlist.id)}`;
}

// A Launchpad's public address: by its tag when it has one, by its IDs
// otherwise. Mirrors canonicalLaunchpadPath in Launchpad-Web.
export function launchpadURL(launchpad: Pick<Launchpad, 'churchID' | 'launchpadID' | 'tag'>) {
  const path = launchpad.tag ?
    `/t/${encodeURIComponent(launchpad.tag)}` :
    `/church/${encodeURIComponent(launchpad.churchID)}/launchpad/${encodeURIComponent(launchpad.launchpadID)}`;

  return `${LAUNCHPAD_WEB_URL}${path}`;
}

function normalizeLinks(links?: LaunchpadLink[] | null): LaunchpadLink[] {
  return [...(links ?? [])].sort((one, two) => one.sortIndex - two.sortIndex);
}

export function normalizeLaunchpadAttributes(attributes: LaunchpadAttributes): LaunchpadAttributes {
  return {
    ...attributes,
    logoURL: attributes.logoURL ?? null,
    campusID: attributes.campusID ?? null,
    categoryID: attributes.categoryID ?? null,
    links: normalizeLinks(attributes.links)
  };
}

export function normalizeLaunchpad(launchpad: Launchpad): Launchpad {
  return {
    ...launchpad,
    metadata: {
      ...launchpad.metadata,
      attributes: normalizeLaunchpadAttributes(launchpad.metadata.attributes)
    }
  };
}

export function launchpadFromMetadata(metadata: LaunchpadMetadata): Launchpad {
  return normalizeLaunchpad({
    churchID: metadata.churchID,
    launchpadID: metadata.launchpadID,
    metadata
  });
}

// Launchpad endpoints aren't consistent about how they wrap a single Launchpad:
// it may arrive under a `result` envelope or not, and as either the wrapped
// Launchpad or bare metadata. Returns null when the payload isn't a Launchpad.
export function launchpadFromResponse(body: unknown): Launchpad | null {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const payload = body as Record<string, any>;
  const candidate = payload.result ?? payload.launchpad ?? payload;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  if (candidate.metadata?.attributes) {
    return normalizeLaunchpad(candidate as Launchpad);
  }

  if (candidate.attributes) {
    return launchpadFromMetadata(candidate as LaunchpadMetadata);
  }

  return null;
}
